import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import config from 'config'
import { orderBy } from 'lodash'
import { insertMessagesInItems } from 'lib/messagesLib'
import { getCarContextKey } from 'lib/carContextKey'

import useSnackbar from '../Snackbar/useSnackbar'

const COMMON_ERROR_FETCHDATA_LABEL = 'common.error.fetchData'
const CARS_TO_BUY_ENDPOINT = 'cars-to-buy'
const CARS_TO_BUY_BLIND_ENDPOINT = 'cars-to-buy-blind'
const BLIND_TYPE_POSITIVE = 'positive'
const BLIND_TYPE_NEGATIVE = 'negative'
const BLIND_MINIMUM_REPRICE_REGEX = /^Blind offer must be at least\s+(\d+)/i
const BLIND_NEGATIVE_MINIMUM_REPRICE_REGEX =
  /^Blind negative offer cannot be below\s+(\d+)/i
const BLIND_NEGATIVE_STEP_ERROR_REGEX =
  /^Blind negative offer must decrement by\s+\d+/i
const BLIND_MATCH_GROUP_PRICE_ERROR_REGEX =
  /^Blind offer cannot match group price/i
const BLIND_REPRICE_REQUIRED_ERROR_REGEX = /^Blind offer price is required$/i
const BLIND_TYPE_COMPATIBILITY_ERROR_REGEX = /additional propert/i
const BLIND_NEGATIVE_MINIMUM_LIMIT_ERROR =
  'common.error.sessions.blindNegativeMinimumLimitNotReached'

const useCarsToBuy = () => {
  const { t } = useTranslation()
  const { showSnackbarMessage } = useSnackbar()

  const translateBlindMinimumOfferMessage = useCallback(
    (message) => {
      if (!message || typeof message !== 'string') {
        return null
      }
      const match =
        message.match(BLIND_MINIMUM_REPRICE_REGEX) ||
        message.match(BLIND_NEGATIVE_MINIMUM_REPRICE_REGEX)
      if (!match) {
        return null
      }
      const amount = Number(match[1])
      const formattedAmount = Number.isFinite(amount)
        ? new Intl.NumberFormat().format(amount)
        : match[1]
      return t('carsToBuy.blind.minimumOffer', { amount: formattedAmount })
    },
    [t]
  )

  const [state, setState] = useState({
    isFetching: false,
    isUpdating: false,
    cars: [],
    total: 0,
    error: null,
  })

  const getBlindTypeFromReprice = useCallback(
    (plate, reprice, sessionId = null) => {
      if (reprice === null || reprice === undefined || reprice === '') {
        return null
      }

      const parsedReprice = Number(reprice)
      if (!Number.isFinite(parsedReprice)) {
        return null
      }

      const contextKey = getCarContextKey({ plate, sessionId }, sessionId)
      const car = state.cars.find(
        (item) => getCarContextKey(item, sessionId) === contextKey
      )
      const parsedBasePrice = Number(car?.price)
      if (!Number.isFinite(parsedBasePrice)) {
        return null
      }

      if (parsedReprice > parsedBasePrice) {
        return BLIND_TYPE_POSITIVE
      }
      if (parsedReprice < parsedBasePrice) {
        return BLIND_TYPE_NEGATIVE
      }
      return null
    },
    [state.cars]
  )

  const isBlindTypeCompatibilityError = useCallback((error) => {
    const status = Number(error?.response?.status)
    const message = error?.response?.data?.message
    return (
      status === 400 &&
      typeof message === 'string' &&
      BLIND_TYPE_COMPATIBILITY_ERROR_REGEX.test(message)
    )
  }, [])

  const postBlindPayloadWithCompatibility = useCallback(
    async (url, payload) => {
      try {
        return await axios.post(url, payload)
      } catch (error) {
        if (!payload?.blindType || !isBlindTypeCompatibilityError(error)) {
          throw error
        }

        const { blindType, ...legacyPayload } = payload
        return axios.post(url, legacyPayload)
      }
    },
    [isBlindTypeCompatibilityError]
  )

  const translateBlindBusinessErrorMessage = useCallback(
    (rawMessage) => {
      if (!rawMessage || typeof rawMessage !== 'string') {
        return null
      }

      if (
        rawMessage ===
        'common.error.sessions.partnerChoiceForSelectedVehicleWouldHitQuota'
      ) {
        return t(rawMessage)
      }

      if (rawMessage === BLIND_NEGATIVE_MINIMUM_LIMIT_ERROR) {
        return t('carsToBuy.blind.minimumLimitNotReached')
      }

      const minimumOfferMessage = translateBlindMinimumOfferMessage(rawMessage)
      if (minimumOfferMessage) {
        return minimumOfferMessage
      }

      if (
        BLIND_NEGATIVE_STEP_ERROR_REGEX.test(rawMessage) ||
        BLIND_MATCH_GROUP_PRICE_ERROR_REGEX.test(rawMessage) ||
        BLIND_REPRICE_REQUIRED_ERROR_REGEX.test(rawMessage)
      ) {
        return t('carsToBuy.blind.minimumInput')
      }

      return null
    },
    [t, translateBlindMinimumOfferMessage]
  )

  const resolveBlindErrorMessage = useCallback(
    (rawMessage, status) =>
      translateBlindBusinessErrorMessage(rawMessage) ||
      t(COMMON_ERROR_FETCHDATA_LABEL, {
        attribute: status,
      }),
    [t, translateBlindBusinessErrorMessage]
  )

  const setStartFetching = useCallback(() => {
    setState((s) => ({
      ...s,
      isFetching: true,
      isUpdating: false,
    }))
  }, [setState])

  const setStopFetching = useCallback(
    (error) => {
      setState((s) => ({
        ...s,
        isFetching: false,
        error: error,
      }))
    },
    [setState]
  )

  const setStartUpdating = useCallback(() => {
    setState((s) => ({
      ...s,
      isFetching: false,
      isUpdating: true,
    }))
  }, [setState])

  const setStopUpdating = useCallback(() => {
    setState((s) => ({
      ...s,
      isUpdating: false,
    }))
  }, [setState])

  const setSuccessFetching = useCallback(
    (data) => {
      setState((s) => ({
        ...s,
        isFetching: false,
        isUpdating: false,
        ...data,
        error: null,
      }))
    },
    [setState]
  )

  const fetchCarsToBuy = useCallback(
    async (sessionId, partnerId, isBlind = false) => {
      setStartFetching()
      try {
        const endpoint = isBlind
          ? CARS_TO_BUY_BLIND_ENDPOINT
          : CARS_TO_BUY_ENDPOINT
        const results = await axios.get(
          `${config.apiBaseUrl}sessions/${sessionId}/partners/${partnerId}/${endpoint}`
        )

        const cars = orderBy(results.data.body.items, ['plate'], ['asc'])

        const messagesResult = await axios.get(
          `${config.apiBaseUrl}sessions/${sessionId}/messages`
        )

        const messages = messagesResult.data.body.messages.filter(
          (message) => message.partnerId === partnerId
        )

        insertMessagesInItems(messages, cars, { fallbackSessionId: sessionId })

        setSuccessFetching({
          cars,
          total: results.data.body.total,
        })
      } catch (e) {
        let message = t(COMMON_ERROR_FETCHDATA_LABEL, {
          attribute: e.response?.status,
        })

        setStopFetching(e)

        showSnackbarMessage({
          severity: 'error',
          message,
        })
      }
    },
    [
      setStartFetching,
      setStopFetching,
      setSuccessFetching,
      showSnackbarMessage,
      t,
    ]
  )

  /* eslint-disable sonarjs/cognitive-complexity */
  const updateChoice = useCallback(
    async (
      sessionId,
      partnerId,
      plate,
      choice,
      isBlind = false,
      reprice,
      blindType = null
    ) => {
      setStartUpdating()

      try {
        const endpoint = isBlind
          ? CARS_TO_BUY_BLIND_ENDPOINT
          : CARS_TO_BUY_ENDPOINT
        const payload = { choice }
        if (reprice !== undefined) {
          payload.reprice = reprice
        }
        if (isBlind && choice === 'first') {
          const resolvedBlindType =
            blindType || getBlindTypeFromReprice(plate, reprice, sessionId)
          if (resolvedBlindType) {
            payload.blindType = resolvedBlindType
          }
        }
        const url = `${config.apiBaseUrl}sessions/${sessionId}/partners/${partnerId}/${endpoint}/${plate}`
        const result =
          isBlind && payload.blindType
            ? await postBlindPayloadWithCompatibility(url, payload)
            : await axios.post(url, payload)
        const {
          data: { body: response },
        } = result
        if (isBlind && response?.success === false) {
          const rawMessage =
            response?.messageString &&
            typeof response.messageString === 'string'
              ? response.messageString
              : response?.message
          const message =
            resolveBlindErrorMessage(rawMessage, result?.status) ||
            t(COMMON_ERROR_FETCHDATA_LABEL, {
              attribute: result?.status,
            })

          showSnackbarMessage({
            severity: 'error',
            message,
          })
        }
        setStopUpdating()
        await fetchCarsToBuy(sessionId, partnerId, isBlind)
        return response.success
      } catch (e) {
        const backendMessage = e.response?.data?.message
        const translatedMessage =
          translateBlindMinimumOfferMessage(backendMessage)
        const message = isBlind
          ? resolveBlindErrorMessage(backendMessage, e.response?.status)
          : translatedMessage ||
            backendMessage ||
            t(COMMON_ERROR_FETCHDATA_LABEL, {
              attribute: e.response?.status,
            })

        setStopUpdating()

        showSnackbarMessage({
          severity: 'error',
          message,
        })
        return null
      }
    },
    [
      fetchCarsToBuy,
      setStartUpdating,
      setStopUpdating,
      showSnackbarMessage,
      t,
      getBlindTypeFromReprice,
      postBlindPayloadWithCompatibility,
      resolveBlindErrorMessage,
      translateBlindMinimumOfferMessage,
    ]
  )
  /* eslint-enable sonarjs/cognitive-complexity */

  const setReprice = useCallback(
    async (sessionId, partnerId, plate, reprice, isBlind = false) => {
      if (isBlind) {
        const hasReprice =
          reprice !== null && reprice !== undefined && reprice !== ''
        const choice = hasReprice ? 'first' : 'none'
        const blindType = hasReprice
          ? getBlindTypeFromReprice(plate, reprice, sessionId)
          : null
        await updateChoice(
          sessionId,
          partnerId,
          plate,
          choice,
          true,
          reprice,
          blindType
        )
        return
      }

      setStartUpdating()

      try {
        await axios.post(
          `${config.apiBaseUrl}sessions/${sessionId}/partners/${partnerId}/${CARS_TO_BUY_ENDPOINT}/${plate}/reprice`,
          { reprice }
        )
        setStopUpdating()
        await fetchCarsToBuy(sessionId, partnerId, isBlind)
      } catch (e) {
        const backendMessage = e.response?.data?.message
        const translatedMessage =
          translateBlindMinimumOfferMessage(backendMessage)
        const message =
          translatedMessage ||
          backendMessage ||
          t(COMMON_ERROR_FETCHDATA_LABEL, {
            attribute: e.response?.status,
          })

        setStopUpdating()

        showSnackbarMessage({
          severity: 'error',
          message,
        })
      }
    },
    [
      fetchCarsToBuy,
      setStartUpdating,
      setStopUpdating,
      showSnackbarMessage,
      t,
      getBlindTypeFromReprice,
      translateBlindMinimumOfferMessage,
      updateChoice,
    ]
  )

  const setBlindPrice = useCallback(
    async (sessionId, partnerId, plate, reprice) => {
      setStartUpdating()

      try {
        const resolvedBlindType = getBlindTypeFromReprice(
          plate,
          reprice,
          sessionId
        )
        const payload = { reprice }
        if (resolvedBlindType) {
          payload.blindType = resolvedBlindType
        }
        const url = `${config.apiBaseUrl}sessions/${sessionId}/partners/${partnerId}/${CARS_TO_BUY_BLIND_ENDPOINT}/${plate}/reprice`
        if (payload.blindType) {
          await postBlindPayloadWithCompatibility(url, payload)
        } else {
          await axios.post(url, payload)
        }
        setStopUpdating()
        await fetchCarsToBuy(sessionId, partnerId, true)
      } catch (e) {
        const rawMessage = e.response?.data?.message
        const message = resolveBlindErrorMessage(rawMessage, e.response?.status)

        setStopUpdating()

        showSnackbarMessage({
          severity: 'error',
          message,
        })
      }
    },
    [
      fetchCarsToBuy,
      setStartUpdating,
      setStopUpdating,
      showSnackbarMessage,
      getBlindTypeFromReprice,
      postBlindPayloadWithCompatibility,
      resolveBlindErrorMessage,
    ]
  )

  const addMessage = useCallback(
    async (sessionId, partnerId, plate, message, isBlind = false) => {
      setStartUpdating()

      try {
        await axios.post(
          `${config.apiBaseUrl}sessions/${sessionId}/partners/${partnerId}/messages/${plate}`,
          { message }
        )
        setStopUpdating()
        await fetchCarsToBuy(sessionId, partnerId, isBlind)
      } catch (e) {
        const message =
          e.response?.data?.message ||
          t(COMMON_ERROR_FETCHDATA_LABEL, {
            attribute: e.response?.status,
          })

        setStopUpdating()

        showSnackbarMessage({
          severity: 'error',
          message,
        })
      }
    },
    [fetchCarsToBuy, setStartUpdating, setStopUpdating, showSnackbarMessage, t]
  )

  const toggleFavorite = useCallback(
    async (sessionId, partnerId, plate, currentFavoriteState) => {
      const newFavoriteState = !currentFavoriteState

      // Optimistic update
      const contextKey = getCarContextKey({ plate, sessionId }, sessionId)
      setState((s) => ({
        ...s,
        cars: s.cars.map((car) =>
          getCarContextKey(car, sessionId) === contextKey
            ? { ...car, favorite: newFavoriteState }
            : car
        ),
      }))

      try {
        await axios.patch(
          `${config.apiBaseUrl}sessions/${sessionId}/partners/${partnerId}/favorites`,
          { plate, favorite: newFavoriteState }
        )
      } catch (e) {
        // Revert on error
        setState((s) => ({
          ...s,
          cars: s.cars.map((car) =>
            getCarContextKey(car, sessionId) === contextKey
              ? { ...car, favorite: currentFavoriteState }
              : car
          ),
        }))

        const message =
          e.response?.data?.message ||
          t(COMMON_ERROR_FETCHDATA_LABEL, {
            attribute: e.response?.status,
          })

        showSnackbarMessage({
          severity: 'error',
          message,
        })
      }
    },
    [showSnackbarMessage, t]
  )

  return {
    cars: state.cars,
    total: state.total,
    isFetching: state.isFetching,
    isUpdating: state.isUpdating,
    error: state.error,
    fetchCarsToBuy,
    updateChoice,
    setReprice,
    setBlindPrice,
    addMessage,
    toggleFavorite,
  }
}

export default useCarsToBuy
